import {Checkbox} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import type {ChecklistRelatedItem, CollectionDataResponse} from "../../types";
import {fileColorMap, fileIconMap, isValidFileType} from "../../contentUtils";
import {buildChecklistDocHref} from "../checklistRefUrl";
import styles from "../checklist-documents-list.module.css";

type DocumentGeneratorRefRowProps = {
    relatedItem: ChecklistRelatedItem;
    collectionData?: CollectionDataResponse;
    onToggle: (topicId: string, selected: boolean) => void;
    checklistBasePath: string;
};

export const DocumentGeneratorRefRow = ({
    relatedItem,
    collectionData,
    onToggle,
    checklistBasePath,
}: DocumentGeneratorRefRowProps) => {
    const {t} = useTranslation();

    const isSelected =
        collectionData?.documents?.some(
            (d) => d.topicId === relatedItem.topicId
        ) ?? false;

    const url = buildChecklistDocHref(
        relatedItem,
        checklistBasePath,
        collectionData
    );

    // PDFs (and any other file type) share this checkbox row with templates
    // because they can be included in generation; mark them with the file
    // icon so they are distinguishable from document templates.
    const FileIcon = isValidFileType(relatedItem.resourceTypeId)
        ? fileIconMap[relatedItem.resourceTypeId]
        : undefined;
    const iconColor = FileIcon
        ? fileColorMap[relatedItem.resourceTypeId]
        : undefined;

    // Opening a template includes it in generation (legacy parity). Guarded
    // here rather than in onToggle: without a filling, opening must do
    // nothing — never open the create-filling modal.
    const handleLinkClick = () => {
        if (!isSelected && collectionData?.metadata) {
            onToggle(relatedItem.topicId, true);
        }
    };

    return (
        <div className={styles.documentRow}>
            <Checkbox
                color="gneis.9"
                styles={{
                    input: {
                        outline: "1px solid var(dib-text-dark-blue-rgb)",
                    },
                }}
                checked={isSelected}
                onChange={() => onToggle(relatedItem.topicId, !isSelected)}
                title={
                    isSelected
                        ? t("Info.RemoveFromGeneration")
                        : t("Info.IncludeInGeneration")
                }
            />
            <Link
                to={url}
                className={styles.documentLink}
                onClick={handleLinkClick}
            >
                {relatedItem.name}
                {FileIcon && (
                    <FileIcon
                        aria-hidden="true"
                        className={styles.documentFileIcon}
                        style={iconColor ? {color: iconColor} : undefined}
                    />
                )}
            </Link>
        </div>
    );
};
